import {
  Clock,
  Webhook,
  Mail,
  Calendar,
  Database,
  FileText,
  Send,
  MessageSquare,
  Bell,
  Globe,
  GitBranch,
  Repeat,
  Filter,
  Shuffle,
  Code,
  Binary,
  FileJson,
  Table,
  Split,
  Merge,
  Hash,
  Link2,
  MessageSquareMore,
  Bot,
  Sparkles,
  Brain,
  Image as ImageIcon,
  MessageCircle,
  FileAudio,
  ShoppingCart,
  Zap,
  Shield,
  Key,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart2,
  Users,
  CreditCard,
  Package,
  Truck,
  Cloud,
  Server,
  Cpu,
  Terminal,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Download,
  Upload,
  Video,
  type LucideIcon
} from 'lucide-react';

export interface ConfigField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'boolean' | 'password' | 'json' | 'code' | 'multiselect';
  required?: boolean;
  placeholder?: string;
  description?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string | number | boolean;
  group?: string; // Para agrupar campos en secciones
}

export interface WorkflowNode {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: LucideIcon;
  color: string;
  configFields: ConfigField[];
  outputs?: string[]; // nombres de los outputs que genera este nodo
  docs?: string; // descripción de uso y ejemplos
}

export interface NodeCategory {
  id: string;
  name: string;
  icon: LucideIcon;
  color: string;
  nodes: WorkflowNode[];
}

export const nodeCategories: NodeCategory[] = [
  // ─── TRIGGERS ────────────────────────────────────────────────────────────────
  {
    id: 'triggers',
    name: 'Triggers',
    icon: Zap,
    color: 'text-blue-500',
    nodes: [
      {
        id: 'trigger_webhook',
        name: 'Webhook',
        description: 'Recibe peticiones HTTP desde servicios externos. Genera una URL única para recibir datos.',
        category: 'triggers',
        icon: Webhook,
        color: 'bg-blue-500',
        outputs: ['body', 'headers', 'query', 'method'],
        docs: 'Genera una URL pública. Úsala en Stripe, GitHub, Shopify, etc. Los datos llegan en {{trigger.body}}',
        configFields: [
          {
            name: 'method',
            label: 'Método HTTP permitido',
            type: 'select',
            required: true,
            defaultValue: 'POST',
            options: [
              { value: 'ANY', label: 'Cualquiera (GET, POST, PUT...)' },
              { value: 'GET', label: 'GET' },
              { value: 'POST', label: 'POST' },
              { value: 'PUT', label: 'PUT' },
              { value: 'PATCH', label: 'PATCH' },
              { value: 'DELETE', label: 'DELETE' },
            ],
          },
          {
            name: 'path',
            label: 'Ruta personalizada',
            type: 'text',
            placeholder: '/mi-webhook',
            description: 'Opcional. Si vacío, se genera automáticamente.',
          },
          {
            name: 'authType',
            label: 'Autenticación',
            type: 'select',
            defaultValue: 'none',
            options: [
              { value: 'none', label: 'Sin autenticación' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth' },
              { value: 'hmac', label: 'HMAC Signature (como Stripe)' },
            ],
          },
          {
            name: 'authSecret',
            label: 'Secret / Token',
            type: 'password',
            placeholder: 'whsec_...',
            description: 'Usado para validar la firma HMAC o el Bearer token',
          },
          {
            name: 'responseMode',
            label: 'Modo de respuesta',
            type: 'select',
            defaultValue: 'lastNode',
            options: [
              { value: 'lastNode', label: 'Responder con output del último nodo' },
              { value: 'custom', label: 'Respuesta personalizada' },
              { value: 'ack', label: 'Solo confirmar recepción (200 OK)' },
            ],
          },
          {
            name: 'customResponse',
            label: 'Respuesta personalizada (JSON)',
            type: 'json',
            placeholder: '{"status": "ok", "message": "Recibido"}',
          },
        ],
      },
      {
        id: 'trigger_schedule',
        name: 'Programado (Cron)',
        description: 'Ejecuta el workflow automáticamente en intervalos definidos',
        category: 'triggers',
        icon: Clock,
        color: 'bg-blue-500',
        outputs: ['timestamp', 'executionCount'],
        docs: 'Ejemplos de cron: "0 9 * * 1-5" = lunes a viernes a las 9am. "*/15 * * * *" = cada 15 minutos.',
        configFields: [
          {
            name: 'mode',
            label: 'Modo',
            type: 'select',
            required: true,
            defaultValue: 'cron',
            options: [
              { value: 'every_minute', label: 'Cada minuto' },
              { value: 'every_5', label: 'Cada 5 minutos' },
              { value: 'every_15', label: 'Cada 15 minutos' },
              { value: 'every_hour', label: 'Cada hora' },
              { value: 'every_day', label: 'Diario' },
              { value: 'every_week', label: 'Semanal' },
              { value: 'every_month', label: 'Mensual' },
              { value: 'cron', label: 'Expresión Cron personalizada' },
            ],
          },
          {
            name: 'cronExpression',
            label: 'Expresión Cron',
            type: 'text',
            placeholder: '0 9 * * 1-5',
            description: 'Minuto Hora DíaMes Mes DíaSemana. Ej: "0 9 * * *" = todos los días a las 9am',
          },
          {
            name: 'timezone',
            label: 'Zona Horaria',
            type: 'select',
            defaultValue: 'Europe/Madrid',
            options: [
              { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
              { value: 'Europe/London', label: 'Londres' },
              { value: 'America/New_York', label: 'Nueva York' },
              { value: 'America/Mexico_City', label: 'Ciudad de México' },
              { value: 'America/Bogota', label: 'Bogotá' },
              { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
              { value: 'Asia/Tokyo', label: 'Tokio' },
              { value: 'UTC', label: 'UTC' },
            ],
          },
          {
            name: 'startDate',
            label: 'Fecha de inicio (opcional)',
            type: 'text',
            placeholder: '2026-01-01',
          },
          {
            name: 'endDate',
            label: 'Fecha de fin (opcional)',
            type: 'text',
            placeholder: '2026-12-31',
          },
        ],
      },
      {
        id: 'trigger_email',
        name: 'Email Recibido (IMAP)',
        description: 'Se activa cuando llega un email que cumple los filtros',
        category: 'triggers',
        icon: Mail,
        color: 'bg-blue-500',
        outputs: ['subject', 'from', 'to', 'body', 'html', 'attachments', 'date'],
        configFields: [
          {
            name: 'host',
            label: 'Servidor IMAP',
            type: 'text',
            required: true,
            placeholder: 'imap.gmail.com',
          },
          {
            name: 'port',
            label: 'Puerto',
            type: 'number',
            defaultValue: 993,
            placeholder: '993',
          },
          {
            name: 'user',
            label: 'Email / Usuario',
            type: 'text',
            required: true,
            placeholder: 'tu@email.com',
          },
          {
            name: 'password',
            label: 'Contraseña / App Password',
            type: 'password',
            required: true,
          },
          {
            name: 'mailbox',
            label: 'Bandeja',
            type: 'text',
            defaultValue: 'INBOX',
            placeholder: 'INBOX',
          },
          {
            name: 'filterFrom',
            label: 'Filtro: De (remitente)',
            type: 'text',
            placeholder: '@empresa.com',
            description: 'Solo emails de este remitente/dominio',
          },
          {
            name: 'filterSubject',
            label: 'Filtro: Asunto contiene',
            type: 'text',
            placeholder: 'Orden confirmada',
          },
          {
            name: 'markAsRead',
            label: 'Marcar como leído',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'downloadAttachments',
            label: 'Descargar adjuntos',
            type: 'boolean',
            defaultValue: false,
          },
        ],
      },
      {
        id: 'trigger_database',
        name: 'Cambio en Base de Datos',
        description: 'Polling a una tabla. Se activa cuando hay registros nuevos o modificados.',
        category: 'triggers',
        icon: Database,
        color: 'bg-blue-500',
        outputs: ['rows', 'rowCount', 'newIds'],
        configFields: [
          {
            name: 'connectionType',
            label: 'Tipo de base de datos',
            type: 'select',
            required: true,
            options: [
              { value: 'postgresql', label: 'PostgreSQL' },
              { value: 'mysql', label: 'MySQL / MariaDB' },
              { value: 'sqlite', label: 'SQLite' },
              { value: 'mssql', label: 'Microsoft SQL Server' },
            ],
          },
          {
            name: 'connectionString',
            label: 'Connection String',
            type: 'password',
            required: true,
            placeholder: 'postgresql://user:pass@host:5432/db',
          },
          {
            name: 'query',
            label: 'Query SQL de polling',
            type: 'code',
            required: true,
            placeholder: 'SELECT * FROM orders WHERE processed = false AND created_at > :lastRun',
            description: 'Usa :lastRun para filtrar desde la última ejecución',
          },
          {
            name: 'pollingInterval',
            label: 'Intervalo de polling (segundos)',
            type: 'number',
            defaultValue: 60,
          },
          {
            name: 'markProcessed',
            label: 'Query para marcar como procesado',
            type: 'code',
            placeholder: 'UPDATE orders SET processed = true WHERE id = :id',
          },
        ],
      },
      {
        id: 'trigger_http_poll',
        name: 'Polling a API REST',
        description: 'Llama a una API periódicamente y activa el workflow cuando hay datos nuevos',
        category: 'triggers',
        icon: RefreshCw,
        color: 'bg-blue-500',
        outputs: ['data', 'statusCode', 'headers'],
        configFields: [
          {
            name: 'url',
            label: 'URL de la API',
            type: 'text',
            required: true,
            placeholder: 'https://api.ejemplo.com/orders?status=pending',
          },
          {
            name: 'method',
            label: 'Método',
            type: 'select',
            defaultValue: 'GET',
            options: [
              { value: 'GET', label: 'GET' },
              { value: 'POST', label: 'POST' },
            ],
          },
          {
            name: 'headers',
            label: 'Headers (JSON)',
            type: 'json',
            placeholder: '{"Authorization": "Bearer {{env.API_KEY}}", "Content-Type": "application/json"}',
          },
          {
            name: 'interval',
            label: 'Intervalo (minutos)',
            type: 'number',
            defaultValue: 5,
          },
          {
            name: 'deduplicationField',
            label: 'Campo para deduplicar',
            type: 'text',
            placeholder: 'id',
            description: 'Campo que identifica registros únicos para no procesar duplicados',
          },
        ],
      },
      {
        id: 'trigger_file',
        name: 'Archivo / FTP / S3',
        description: 'Detecta archivos nuevos en carpeta local, FTP o bucket S3',
        category: 'triggers',
        icon: FileText,
        color: 'bg-blue-500',
        outputs: ['fileName', 'filePath', 'fileSize', 'mimeType', 'content'],
        configFields: [
          {
            name: 'source',
            label: 'Fuente',
            type: 'select',
            required: true,
            options: [
              { value: 'local', label: 'Carpeta local' },
              { value: 'ftp', label: 'FTP / SFTP' },
              { value: 's3', label: 'Amazon S3' },
              { value: 'gdrive', label: 'Google Drive' },
              { value: 'dropbox', label: 'Dropbox' },
            ],
          },
          {
            name: 'path',
            label: 'Ruta / Bucket / Carpeta',
            type: 'text',
            required: true,
            placeholder: '/uploads o mi-bucket/carpeta',
          },
          {
            name: 'filePattern',
            label: 'Patrón de archivo',
            type: 'text',
            placeholder: '*.csv o invoice_*.pdf',
            description: 'Glob pattern para filtrar archivos',
          },
          {
            name: 'readContent',
            label: 'Leer contenido del archivo',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'moveAfterProcess',
            label: 'Mover archivo después de procesar',
            type: 'text',
            placeholder: '/processed',
            description: 'Carpeta destino. Vacío = no mover.',
          },
        ],
      },
    ],
  },

  // ─── ACTIONS ─────────────────────────────────────────────────────────────────
  {
    id: 'actions',
    name: 'Acciones',
    icon: Send,
    color: 'text-green-500',
    nodes: [
      {
        id: 'action_email',
        name: 'Enviar Email (SMTP)',
        description: 'Envía emails con plantillas HTML, adjuntos y variables dinámicas',
        category: 'actions',
        icon: Mail,
        color: 'bg-green-500',
        outputs: ['messageId', 'success'],
        docs: 'Usa {{variable}} para insertar datos del workflow. Ej: "Hola {{data.nombre}}, tu orden {{data.orderId}} está lista"',
        configFields: [
          {
            name: 'to',
            label: 'Para (To)',
            type: 'text',
            required: true,
            placeholder: '{{data.email}} o cliente@empresa.com',
            description: 'Soporta variables. Múltiples: separa con coma',
          },
          {
            name: 'cc',
            label: 'CC',
            type: 'text',
            placeholder: 'supervisor@empresa.com',
          },
          {
            name: 'bcc',
            label: 'BCC',
            type: 'text',
            placeholder: 'archivo@empresa.com',
          },
          {
            name: 'from',
            label: 'De (From)',
            type: 'text',
            placeholder: 'ZAPHUB <noreply@empresa.com>',
          },
          {
            name: 'subject',
            label: 'Asunto',
            type: 'text',
            required: true,
            placeholder: 'Tu orden #{{data.orderId}} ha sido confirmada',
          },
          {
            name: 'bodyType',
            label: 'Tipo de mensaje',
            type: 'select',
            defaultValue: 'html',
            options: [
              { value: 'html', label: 'HTML' },
              { value: 'text', label: 'Texto plano' },
              { value: 'template', label: 'Plantilla guardada' },
            ],
          },
          {
            name: 'body',
            label: 'Cuerpo del mensaje',
            type: 'textarea',
            required: true,
            placeholder: '<h1>Hola {{data.nombre}}</h1><p>Tu pedido está en camino.</p>',
          },
          {
            name: 'smtpHost',
            label: 'Servidor SMTP',
            type: 'text',
            placeholder: 'smtp.gmail.com',
            group: 'SMTP',
          },
          {
            name: 'smtpPort',
            label: 'Puerto SMTP',
            type: 'number',
            defaultValue: 587,
            group: 'SMTP',
          },
          {
            name: 'smtpUser',
            label: 'Usuario SMTP',
            type: 'text',
            placeholder: 'tu@gmail.com',
            group: 'SMTP',
          },
          {
            name: 'smtpPass',
            label: 'Contraseña SMTP',
            type: 'password',
            group: 'SMTP',
          },
          {
            name: 'retryOnFail',
            label: 'Reintentar si falla',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'retries',
            label: 'Número de reintentos',
            type: 'number',
            defaultValue: 3,
          },
        ],
      },
      {
        id: 'action_http',
        name: 'Petición HTTP / API',
        description: 'Llama a cualquier API REST o GraphQL con headers, auth y body personalizados',
        category: 'actions',
        icon: Globe,
        color: 'bg-green-500',
        outputs: ['body', 'statusCode', 'headers', 'responseTime'],
        docs: 'El resultado estará en {{nodo.body}}. Soporta OAuth2, Bearer, Basic, API Key.',
        configFields: [
          {
            name: 'method',
            label: 'Método HTTP',
            type: 'select',
            required: true,
            defaultValue: 'POST',
            options: [
              { value: 'GET', label: 'GET' },
              { value: 'POST', label: 'POST' },
              { value: 'PUT', label: 'PUT' },
              { value: 'PATCH', label: 'PATCH' },
              { value: 'DELETE', label: 'DELETE' },
              { value: 'HEAD', label: 'HEAD' },
            ],
          },
          {
            name: 'url',
            label: 'URL',
            type: 'text',
            required: true,
            placeholder: 'https://api.ejemplo.com/v1/{{data.id}}/update',
            description: 'Soporta variables dinámicas {{data.campo}}',
          },
          {
            name: 'authType',
            label: 'Autenticación',
            type: 'select',
            defaultValue: 'none',
            options: [
              { value: 'none', label: 'Sin autenticación' },
              { value: 'bearer', label: 'Bearer Token' },
              { value: 'basic', label: 'Basic Auth (usuario:contraseña)' },
              { value: 'apikey_header', label: 'API Key en Header' },
              { value: 'apikey_query', label: 'API Key en Query Param' },
            ],
          },
          {
            name: 'authToken',
            label: 'Token / API Key',
            type: 'password',
            placeholder: '{{env.MY_API_KEY}}',
            description: 'Puedes referenciar variables de entorno con {{env.NOMBRE}}',
          },
          {
            name: 'authKeyName',
            label: 'Nombre del header/param',
            type: 'text',
            placeholder: 'X-API-Key',
            description: 'Solo para API Key — nombre del header o query param',
          },
          {
            name: 'headers',
            label: 'Headers adicionales (JSON)',
            type: 'json',
            placeholder: '{"Content-Type": "application/json", "X-Custom": "valor"}',
          },
          {
            name: 'queryParams',
            label: 'Query Parameters (JSON)',
            type: 'json',
            placeholder: '{"limit": 100, "status": "active"}',
          },
          {
            name: 'body',
            label: 'Body (JSON)',
            type: 'json',
            placeholder: '{"name": "{{data.nombre}}", "email": "{{data.email}}"}',
          },
          {
            name: 'timeout',
            label: 'Timeout (segundos)',
            type: 'number',
            defaultValue: 30,
          },
          {
            name: 'retries',
            label: 'Reintentos si falla',
            type: 'number',
            defaultValue: 0,
          },
          {
            name: 'followRedirects',
            label: 'Seguir redirects',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'ignoreErrors',
            label: 'Continuar aunque falle (no lanzar error)',
            type: 'boolean',
            defaultValue: false,
          },
        ],
      },
      {
        id: 'action_database',
        name: 'Base de Datos (SQL)',
        description: 'Ejecuta queries SQL: SELECT, INSERT, UPDATE, DELETE en cualquier BD',
        category: 'actions',
        icon: Database,
        color: 'bg-green-500',
        outputs: ['rows', 'rowCount', 'lastInsertId'],
        configFields: [
          {
            name: 'connectionType',
            label: 'Tipo de base de datos',
            type: 'select',
            required: true,
            options: [
              { value: 'postgresql', label: 'PostgreSQL' },
              { value: 'mysql', label: 'MySQL / MariaDB' },
              { value: 'sqlite', label: 'SQLite' },
              { value: 'mssql', label: 'SQL Server' },
            ],
          },
          {
            name: 'connectionString',
            label: 'Connection String',
            type: 'password',
            required: true,
            placeholder: 'postgresql://user:pass@host:5432/db',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'select', label: 'SELECT — Consultar datos' },
              { value: 'insert', label: 'INSERT — Insertar registro' },
              { value: 'update', label: 'UPDATE — Actualizar registros' },
              { value: 'delete', label: 'DELETE — Eliminar registros' },
              { value: 'raw', label: 'SQL Personalizado' },
            ],
          },
          {
            name: 'table',
            label: 'Tabla',
            type: 'text',
            placeholder: 'orders',
            description: 'Solo para INSERT/UPDATE/DELETE simple',
          },
          {
            name: 'query',
            label: 'Query SQL',
            type: 'code',
            required: true,
            placeholder: 'SELECT * FROM orders WHERE status = :status AND user_id = :userId',
            description: 'Usa :parametro para variables seguras (previene SQL injection)',
          },
          {
            name: 'parameters',
            label: 'Parámetros (JSON)',
            type: 'json',
            placeholder: '{"status": "{{data.status}}", "userId": "{{data.userId}}"}',
          },
          {
            name: 'returnAll',
            label: 'Devolver todos los resultados',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'limit',
            label: 'Límite de resultados',
            type: 'number',
            defaultValue: 1000,
          },
        ],
      },
      {
        id: 'action_sms',
        name: 'Enviar SMS (Twilio)',
        description: 'Envía SMS a cualquier número vía Twilio',
        category: 'actions',
        icon: MessageSquare,
        color: 'bg-green-500',
        outputs: ['sid', 'status', 'success'],
        configFields: [
          {
            name: 'accountSid',
            label: 'Twilio Account SID',
            type: 'password',
            required: true,
            placeholder: 'AC...',
          },
          {
            name: 'authToken',
            label: 'Twilio Auth Token',
            type: 'password',
            required: true,
          },
          {
            name: 'from',
            label: 'Número origen (Twilio)',
            type: 'text',
            required: true,
            placeholder: '+14155238886',
          },
          {
            name: 'to',
            label: 'Número destino',
            type: 'text',
            required: true,
            placeholder: '{{data.phone}} o +34600000000',
          },
          {
            name: 'message',
            label: 'Mensaje',
            type: 'textarea',
            required: true,
            placeholder: 'Hola {{data.nombre}}, tu código es {{data.code}}',
            description: 'Máximo 160 caracteres para SMS estándar',
          },
        ],
      },
      {
        id: 'action_notification',
        name: 'Notificación Push',
        description: 'Envía notificación in-app o push a usuarios de ZAPHUB',
        category: 'actions',
        icon: Bell,
        color: 'bg-green-500',
        outputs: ['notificationId', 'success'],
        configFields: [
          {
            name: 'userId',
            label: 'Usuario destino',
            type: 'text',
            required: true,
            placeholder: '{{data.userId}} o ID específico',
            description: 'ID del usuario ZAPHUB que recibirá la notificación',
          },
          {
            name: 'title',
            label: 'Título',
            type: 'text',
            required: true,
            placeholder: '✅ Tarea completada: {{data.taskName}}',
          },
          {
            name: 'message',
            label: 'Mensaje',
            type: 'textarea',
            required: true,
            placeholder: 'El workflow "{{data.workflowName}}" se ejecutó correctamente.',
          },
          {
            name: 'type',
            label: 'Tipo',
            type: 'select',
            defaultValue: 'INFO',
            options: [
              { value: 'INFO', label: 'Información' },
              { value: 'SUCCESS', label: 'Éxito' },
              { value: 'WARNING', label: 'Advertencia' },
              { value: 'ERROR', label: 'Error' },
            ],
          },
          {
            name: 'link',
            label: 'Link al hacer clic (opcional)',
            type: 'text',
            placeholder: '/dashboard/workflows/{{data.workflowId}}',
          },
        ],
      },
      {
        id: 'action_file_write',
        name: 'Escribir / Subir Archivo',
        description: 'Crea archivos locales, sube a S3, Google Drive o FTP',
        category: 'actions',
        icon: Upload,
        color: 'bg-green-500',
        outputs: ['filePath', 'fileUrl', 'fileSize', 'success'],
        configFields: [
          {
            name: 'destination',
            label: 'Destino',
            type: 'select',
            required: true,
            options: [
              { value: 'local', label: 'Archivo local' },
              { value: 's3', label: 'Amazon S3' },
              { value: 'gdrive', label: 'Google Drive' },
              { value: 'ftp', label: 'FTP / SFTP' },
            ],
          },
          {
            name: 'path',
            label: 'Ruta / Clave S3',
            type: 'text',
            required: true,
            placeholder: '/output/report-{{date}}.csv',
          },
          {
            name: 'content',
            label: 'Contenido',
            type: 'textarea',
            required: true,
            placeholder: '{{data.csvContent}} o texto estático',
          },
          {
            name: 'contentType',
            label: 'Tipo de contenido',
            type: 'select',
            options: [
              { value: 'text/plain', label: 'Texto plano (.txt)' },
              { value: 'text/csv', label: 'CSV' },
              { value: 'application/json', label: 'JSON' },
              { value: 'text/html', label: 'HTML' },
              { value: 'application/pdf', label: 'PDF' },
            ],
          },
          {
            name: 'mode',
            label: 'Modo',
            type: 'select',
            defaultValue: 'create',
            options: [
              { value: 'create', label: 'Crear nuevo (falla si existe)' },
              { value: 'overwrite', label: 'Sobreescribir' },
              { value: 'append', label: 'Agregar al final' },
            ],
          },
          {
            name: 's3Bucket',
            label: 'Bucket S3',
            type: 'text',
            placeholder: 'mi-bucket-produccion',
            group: 'S3',
          },
          {
            name: 's3Region',
            label: 'Región S3',
            type: 'text',
            placeholder: 'eu-west-1',
            group: 'S3',
          },
        ],
      },
    ],
  },

  // ─── LOGIC ───────────────────────────────────────────────────────────────────
  {
    id: 'logic',
    name: 'Lógica',
    icon: GitBranch,
    color: 'text-orange-500',
    nodes: [
      {
        id: 'logic_if',
        name: 'Condición If / Else',
        description: 'Bifurca el flujo según una condición. Rama TRUE o FALSE.',
        category: 'logic',
        icon: GitBranch,
        color: 'bg-orange-500',
        outputs: ['true', 'false'],
        docs: 'Ejemplos: data.amount > 1000 | data.status === "approved" | data.email.includes("@gmail.com")',
        configFields: [
          {
            name: 'mode',
            label: 'Modo de condición',
            type: 'select',
            defaultValue: 'expression',
            options: [
              { value: 'expression', label: 'Expresión JavaScript' },
              { value: 'rules', label: 'Reglas visuales (sin código)' },
            ],
          },
          {
            name: 'condition',
            label: 'Expresión (JavaScript)',
            type: 'code',
            required: true,
            placeholder: 'data.amount > 1000 && data.status === "pending"',
            description: 'Accede a los datos con "data". Debe devolver true o false.',
          },
          {
            name: 'field',
            label: '[Reglas] Campo',
            type: 'text',
            placeholder: 'data.status',
          },
          {
            name: 'operator',
            label: '[Reglas] Operador',
            type: 'select',
            options: [
              { value: 'equals', label: 'Es igual a' },
              { value: 'not_equals', label: 'No es igual a' },
              { value: 'contains', label: 'Contiene' },
              { value: 'not_contains', label: 'No contiene' },
              { value: 'greater', label: 'Mayor que' },
              { value: 'less', label: 'Menor que' },
              { value: 'is_empty', label: 'Está vacío' },
              { value: 'is_not_empty', label: 'No está vacío' },
              { value: 'starts_with', label: 'Empieza por' },
              { value: 'ends_with', label: 'Termina en' },
            ],
          },
          {
            name: 'value',
            label: '[Reglas] Valor',
            type: 'text',
            placeholder: 'aprobado',
          },
        ],
      },
      {
        id: 'logic_switch',
        name: 'Switch / Router',
        description: 'Enruta el flujo a múltiples ramas según el valor de un campo',
        category: 'logic',
        icon: Shuffle,
        color: 'bg-orange-500',
        outputs: ['branch_1', 'branch_2', 'branch_3', 'default'],
        configFields: [
          {
            name: 'field',
            label: 'Campo a evaluar',
            type: 'text',
            required: true,
            placeholder: 'data.status o data.country',
          },
          {
            name: 'cases',
            label: 'Casos (JSON)',
            type: 'json',
            required: true,
            placeholder: '{\n  "pending": "branch_1",\n  "approved": "branch_2",\n  "rejected": "branch_3"\n}',
            description: 'Mapa de valor → nombre de rama',
          },
          {
            name: 'fallthrough',
            label: 'Continuar si no hay coincidencia',
            type: 'boolean',
            defaultValue: true,
            description: 'Si activado, usa la rama "default"',
          },
        ],
      },
      {
        id: 'logic_loop',
        name: 'Loop (For Each)',
        description: 'Itera sobre cada elemento de un array y ejecuta los nodos siguientes para cada uno',
        category: 'logic',
        icon: Repeat,
        color: 'bg-orange-500',
        outputs: ['item', 'index', 'total', 'results'],
        docs: 'Dentro del loop, usa {{loop.item}} para el elemento actual y {{loop.index}} para el índice.',
        configFields: [
          {
            name: 'array',
            label: 'Array a iterar',
            type: 'text',
            required: true,
            placeholder: '{{data.items}} o {{data.orders}}',
            description: 'Expresión que devuelve el array',
          },
          {
            name: 'batchSize',
            label: 'Tamaño de lote (batch)',
            type: 'number',
            defaultValue: 1,
            description: 'Procesar N elementos en paralelo. 1 = secuencial.',
          },
          {
            name: 'maxIterations',
            label: 'Límite máximo de iteraciones',
            type: 'number',
            defaultValue: 1000,
          },
          {
            name: 'continueOnError',
            label: 'Continuar si un item falla',
            type: 'boolean',
            defaultValue: false,
          },
          {
            name: 'collectResults',
            label: 'Recopilar resultados en array',
            type: 'boolean',
            defaultValue: true,
          },
        ],
      },
      {
        id: 'logic_filter',
        name: 'Filtrar Array',
        description: 'Filtra elementos de un array según una condición. Solo pasan los que cumplen.',
        category: 'logic',
        icon: Filter,
        color: 'bg-orange-500',
        outputs: ['items', 'count', 'filtered'],
        configFields: [
          {
            name: 'array',
            label: 'Array a filtrar',
            type: 'text',
            required: true,
            placeholder: '{{data.orders}}',
          },
          {
            name: 'condition',
            label: 'Condición (JavaScript)',
            type: 'code',
            required: true,
            placeholder: 'item.amount > 100 && item.status === "pending"',
            description: 'Usa "item" para el elemento actual. Devuelve true para incluirlo.',
          },
          {
            name: 'outputEmpty',
            label: 'Continuar si el resultado está vacío',
            type: 'boolean',
            defaultValue: true,
          },
        ],
      },
      {
        id: 'logic_delay',
        name: 'Esperar / Delay',
        description: 'Pausa la ejecución del workflow por tiempo definido o hasta una fecha',
        category: 'logic',
        icon: Clock,
        color: 'bg-orange-500',
        configFields: [
          {
            name: 'mode',
            label: 'Modo',
            type: 'select',
            required: true,
            defaultValue: 'seconds',
            options: [
              { value: 'seconds', label: 'Segundos' },
              { value: 'minutes', label: 'Minutos' },
              { value: 'hours', label: 'Horas' },
              { value: 'until', label: 'Hasta fecha/hora específica' },
              { value: 'until_field', label: 'Hasta fecha en campo de datos' },
            ],
          },
          {
            name: 'duration',
            label: 'Duración',
            type: 'number',
            placeholder: '5',
            description: 'Número de segundos/minutos/horas a esperar',
          },
          {
            name: 'untilDate',
            label: 'Fecha y hora destino',
            type: 'text',
            placeholder: '2026-12-31T09:00:00Z',
          },
          {
            name: 'untilField',
            label: 'Campo con la fecha',
            type: 'text',
            placeholder: '{{data.scheduledAt}}',
          },
        ],
      },
      {
        id: 'logic_merge',
        name: 'Merge / Join',
        description: 'Une datos de múltiples ramas del workflow en uno solo',
        category: 'logic',
        icon: Merge,
        color: 'bg-orange-500',
        outputs: ['merged'],
        configFields: [
          {
            name: 'mode',
            label: 'Modo de unión',
            type: 'select',
            required: true,
            options: [
              { value: 'merge_objects', label: 'Combinar objetos (shallow merge)' },
              { value: 'deep_merge', label: 'Deep merge (nested)' },
              { value: 'concat_arrays', label: 'Concatenar arrays' },
              { value: 'zip', label: 'Zip (combinar por índice)' },
              { value: 'wait_all', label: 'Esperar todas las ramas' },
            ],
          },
          {
            name: 'outputKey',
            label: 'Clave de agrupación (opcional)',
            type: 'text',
            placeholder: 'id',
            description: 'Para Join: campo en común entre las fuentes',
          },
        ],
      },
      {
        id: 'logic_error_handler',
        name: 'Manejo de Errores',
        description: 'Captura errores del nodo anterior y decide cómo continuar',
        category: 'logic',
        icon: AlertTriangle,
        color: 'bg-orange-500',
        outputs: ['error', 'message', 'retry'],
        configFields: [
          {
            name: 'action',
            label: 'Acción al capturar error',
            type: 'select',
            required: true,
            options: [
              { value: 'continue', label: 'Continuar con datos vacíos' },
              { value: 'retry', label: 'Reintentar N veces' },
              { value: 'branch', label: 'Ir a rama de error' },
              { value: 'stop', label: 'Detener workflow' },
            ],
          },
          {
            name: 'maxRetries',
            label: 'Máximo de reintentos',
            type: 'number',
            defaultValue: 3,
          },
          {
            name: 'retryDelay',
            label: 'Espera entre reintentos (segundos)',
            type: 'number',
            defaultValue: 5,
          },
          {
            name: 'notifyOnError',
            label: 'Notificar al admin si falla',
            type: 'boolean',
            defaultValue: false,
          },
        ],
      },
    ],
  },

  // ─── DATA TRANSFORMATION ─────────────────────────────────────────────────────
  {
    id: 'data',
    name: 'Transformación de Datos',
    icon: Code,
    color: 'text-purple-500',
    nodes: [
      {
        id: 'data_transform',
        name: 'Código JavaScript',
        description: 'Transforma datos con código JS. Acceso completo a los datos del workflow.',
        category: 'data',
        icon: Code,
        color: 'bg-purple-500',
        outputs: ['result'],
        docs: 'Recibe "items" (array de datos) y "env" (variables de entorno). Devuelve el resultado con return.',
        configFields: [
          {
            name: 'code',
            label: 'Código JavaScript',
            type: 'code',
            required: true,
            placeholder: `// Ejemplo: Transformar array de órdenes
const orders = items[0].data.orders;

return orders.map(order => ({
  id: order.id,
  total: order.items.reduce((sum, i) => sum + i.price, 0),
  status: order.amount > 1000 ? 'vip' : 'normal',
  createdAt: new Date(order.created_at).toISOString()
}));`,
            description: 'Usa "items" para los datos de entrada. Devuelve el resultado con "return".',
          },
          {
            name: 'continueOnFail',
            label: 'Continuar si el código lanza error',
            type: 'boolean',
            defaultValue: false,
          },
        ],
      },
      {
        id: 'data_json_parse',
        name: 'Parsear / Formatear JSON',
        description: 'Convierte string JSON a objeto o viceversa. Extrae campos con JSONPath.',
        category: 'data',
        icon: FileJson,
        color: 'bg-purple-500',
        outputs: ['parsed', 'field'],
        configFields: [
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'parse', label: 'String → Objeto (parse)' },
              { value: 'stringify', label: 'Objeto → String (stringify)' },
              { value: 'extract', label: 'Extraer campo con JSONPath' },
              { value: 'flatten', label: 'Aplanar objeto anidado' },
            ],
          },
          {
            name: 'data',
            label: 'Dato de entrada',
            type: 'text',
            required: true,
            placeholder: '{{data.rawJson}}',
          },
          {
            name: 'path',
            label: 'JSONPath (para extract)',
            type: 'text',
            placeholder: '$.orders[*].id',
            description: 'Expresión JSONPath. Ej: $.user.address.city',
          },
          {
            name: 'pretty',
            label: 'Formato bonito (pretty print)',
            type: 'boolean',
            defaultValue: false,
          },
        ],
      },
      {
        id: 'data_csv',
        name: 'CSV — Parsear / Generar',
        description: 'Convierte CSV a JSON o JSON a CSV. Listo para reportes.',
        category: 'data',
        icon: Table,
        color: 'bg-purple-500',
        outputs: ['rows', 'csvString', 'headers'],
        configFields: [
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'to_json', label: 'CSV → JSON (parsear)' },
              { value: 'from_json', label: 'JSON → CSV (generar)' },
            ],
          },
          {
            name: 'data',
            label: 'Datos de entrada',
            type: 'textarea',
            required: true,
            placeholder: '{{data.csvContent}} o {{data.rows}}',
          },
          {
            name: 'delimiter',
            label: 'Delimitador',
            type: 'select',
            defaultValue: ',',
            options: [
              { value: ',', label: 'Coma (,)' },
              { value: ';', label: 'Punto y coma (;)' },
              { value: '\t', label: 'Tabulador' },
              { value: '|', label: 'Pipe (|)' },
            ],
          },
          {
            name: 'hasHeader',
            label: 'Primera fila es cabecera',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'columns',
            label: 'Columnas a incluir (separadas por coma)',
            type: 'text',
            placeholder: 'id,name,email,amount',
            description: 'Vacío = todas las columnas',
          },
        ],
      },
      {
        id: 'data_aggregate',
        name: 'Agregar / Calcular',
        description: 'Suma, promedio, máximo, mínimo, contar sobre arrays de datos',
        category: 'data',
        icon: BarChart2,
        color: 'bg-purple-500',
        outputs: ['result', 'grouped'],
        configFields: [
          {
            name: 'array',
            label: 'Array de datos',
            type: 'text',
            required: true,
            placeholder: '{{data.orders}}',
          },
          {
            name: 'operations',
            label: 'Operaciones (JSON)',
            type: 'json',
            required: true,
            placeholder: `[
  {"field": "amount", "operation": "sum", "as": "total"},
  {"field": "amount", "operation": "avg", "as": "avgAmount"},
  {"field": "id", "operation": "count", "as": "orderCount"}
]`,
            description: 'Operations: sum, avg, min, max, count, first, last',
          },
          {
            name: 'groupBy',
            label: 'Agrupar por campo (opcional)',
            type: 'text',
            placeholder: 'status',
            description: 'Si se especifica, agrupa los resultados por este campo',
          },
        ],
      },
      {
        id: 'data_set_fields',
        name: 'Establecer Campos',
        description: 'Crea, renombra o elimina campos en los datos. Sin código.',
        category: 'data',
        icon: Binary,
        color: 'bg-purple-500',
        outputs: ['data'],
        configFields: [
          {
            name: 'assignments',
            label: 'Asignaciones (JSON)',
            type: 'json',
            required: true,
            placeholder: `{
  "fullName": "{{data.firstName}} {{data.lastName}}",
  "totalWithTax": "{{data.amount * 1.21}}",
  "processedAt": "{{$now}}",
  "source": "zaphub"
}`,
            description: 'Clave = nombre del campo. Valor = valor o expresión con {{}}.',
          },
          {
            name: 'removeFields',
            label: 'Campos a eliminar (separados por coma)',
            type: 'text',
            placeholder: 'password,token,internalId',
          },
          {
            name: 'keepOnly',
            label: 'Solo mantener estos campos',
            type: 'text',
            placeholder: 'id,name,email,amount',
          },
        ],
      },
      {
        id: 'data_split_merge',
        name: 'Dividir / Agrupar Array',
        description: 'Divide un array en lotes o agrupa múltiples arrays en uno',
        category: 'data',
        icon: Split,
        color: 'bg-purple-500',
        outputs: ['chunks', 'merged'],
        configFields: [
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'split', label: 'Dividir en lotes' },
              { value: 'flatten', label: 'Aplanar array de arrays' },
              { value: 'unique', label: 'Eliminar duplicados' },
              { value: 'sort', label: 'Ordenar' },
            ],
          },
          {
            name: 'array',
            label: 'Array',
            type: 'text',
            required: true,
            placeholder: '{{data.items}}',
          },
          {
            name: 'chunkSize',
            label: 'Tamaño del lote',
            type: 'number',
            defaultValue: 10,
            description: 'Solo para operación split',
          },
          {
            name: 'sortField',
            label: 'Campo para ordenar',
            type: 'text',
            placeholder: 'createdAt',
          },
          {
            name: 'sortOrder',
            label: 'Orden',
            type: 'select',
            options: [
              { value: 'asc', label: 'Ascendente (A→Z, 0→9)' },
              { value: 'desc', label: 'Descendente (Z→A, 9→0)' },
            ],
          },
        ],
      },
    ],
  },

  // ─── INTEGRATIONS ─────────────────────────────────────────────────────────────
  {
    id: 'integrations',
    name: 'Integraciones',
    icon: Globe,
    color: 'text-cyan-500',
    nodes: [
      {
        id: 'int_slack',
        name: 'Slack',
        description: 'Envía mensajes, crea canales, gestiona usuarios en Slack',
        category: 'integrations',
        icon: Hash,
        color: 'bg-cyan-500',
        outputs: ['messageId', 'timestamp', 'success'],
        configFields: [
          {
            name: 'botToken',
            label: 'Bot Token (xoxb-...)',
            type: 'password',
            required: true,
            placeholder: 'xoxb-...',
            description: 'Token del Bot en https://api.slack.com/apps',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            defaultValue: 'send_message',
            options: [
              { value: 'send_message', label: 'Enviar mensaje a canal' },
              { value: 'send_dm', label: 'Enviar DM a usuario' },
              { value: 'update_message', label: 'Actualizar mensaje' },
              { value: 'upload_file', label: 'Subir archivo/imagen' },
              { value: 'create_channel', label: 'Crear canal' },
              { value: 'invite_user', label: 'Invitar usuario a canal' },
            ],
          },
          {
            name: 'channel',
            label: 'Canal o User ID',
            type: 'text',
            required: true,
            placeholder: '#alertas o {{data.slackUserId}}',
          },
          {
            name: 'message',
            label: 'Mensaje',
            type: 'textarea',
            required: true,
            placeholder: '🚨 Alerta: {{data.errorMessage}}\nWorkflow: {{data.workflowName}}',
          },
          {
            name: 'blocks',
            label: 'Blocks (JSON — layout avanzado)',
            type: 'json',
            placeholder: '[]',
            description: 'Slack Block Kit para mensajes ricos con botones, imágenes, etc.',
          },
          {
            name: 'threadTs',
            label: 'Responder en hilo (timestamp)',
            type: 'text',
            placeholder: '{{data.slackTs}}',
            description: 'Para responder a un mensaje existente',
          },
          {
            name: 'username',
            label: 'Nombre del bot (override)',
            type: 'text',
            placeholder: 'ZAPHUB Bot',
          },
          {
            name: 'iconEmoji',
            label: 'Icono (emoji)',
            type: 'text',
            placeholder: ':robot_face:',
          },
        ],
      },
      {
        id: 'int_github',
        name: 'GitHub',
        description: 'Gestiona issues, PRs, commits, releases en repositorios GitHub',
        category: 'integrations',
        icon: GitBranch,
        color: 'bg-cyan-500',
        outputs: ['data', 'id', 'url', 'success'],
        configFields: [
          {
            name: 'token',
            label: 'Personal Access Token',
            type: 'password',
            required: true,
            placeholder: 'ghp_...',
            description: 'Con permisos repo, issues, pull_requests',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'create_issue', label: 'Crear Issue' },
              { value: 'update_issue', label: 'Actualizar Issue' },
              { value: 'close_issue', label: 'Cerrar Issue' },
              { value: 'create_pr', label: 'Crear Pull Request' },
              { value: 'merge_pr', label: 'Hacer Merge de PR' },
              { value: 'add_comment', label: 'Agregar Comentario' },
              { value: 'add_label', label: 'Agregar Etiqueta' },
              { value: 'get_file', label: 'Obtener Contenido de Archivo' },
              { value: 'create_file', label: 'Crear/Actualizar Archivo' },
              { value: 'create_release', label: 'Crear Release' },
              { value: 'trigger_workflow', label: 'Disparar GitHub Actions' },
            ],
          },
          {
            name: 'repository',
            label: 'Repositorio (owner/repo)',
            type: 'text',
            required: true,
            placeholder: 'miempresa/mi-proyecto',
          },
          {
            name: 'title',
            label: 'Título',
            type: 'text',
            placeholder: 'Bug: {{data.errorTitle}}',
          },
          {
            name: 'body',
            label: 'Cuerpo / Descripción',
            type: 'textarea',
            placeholder: '## Descripción\n{{data.description}}\n\n## Pasos para reproducir\n{{data.steps}}',
          },
          {
            name: 'labels',
            label: 'Etiquetas (separadas por coma)',
            type: 'text',
            placeholder: 'bug,urgente,{{data.priority}}',
          },
          {
            name: 'assignees',
            label: 'Asignar a (usernames)',
            type: 'text',
            placeholder: 'usuario1,usuario2',
          },
          {
            name: 'issueNumber',
            label: 'Número de Issue/PR',
            type: 'text',
            placeholder: '{{data.issueNumber}}',
          },
        ],
      },
      {
        id: 'int_discord',
        name: 'Discord',
        description: 'Envía mensajes y embeds a canales de Discord vía Webhook o Bot',
        category: 'integrations',
        icon: MessageSquareMore,
        color: 'bg-cyan-500',
        outputs: ['messageId', 'success'],
        configFields: [
          {
            name: 'mode',
            label: 'Modo de conexión',
            type: 'select',
            required: true,
            options: [
              { value: 'webhook', label: 'Webhook URL (simple)' },
              { value: 'bot', label: 'Bot Token (avanzado)' },
            ],
          },
          {
            name: 'webhookUrl',
            label: 'Webhook URL',
            type: 'password',
            placeholder: 'https://discord.com/api/webhooks/...',
          },
          {
            name: 'botToken',
            label: 'Bot Token',
            type: 'password',
            placeholder: 'MTI...',
          },
          {
            name: 'channelId',
            label: 'Channel ID',
            type: 'text',
            placeholder: '1234567890',
          },
          {
            name: 'content',
            label: 'Mensaje',
            type: 'textarea',
            placeholder: '🔔 Alerta: {{data.message}}',
          },
          {
            name: 'embed',
            label: 'Embed (JSON — mensaje enriquecido)',
            type: 'json',
            placeholder: `{
  "title": "{{data.title}}",
  "description": "{{data.body}}",
  "color": 5763719,
  "fields": [
    {"name": "Estado", "value": "{{data.status}}", "inline": true}
  ]
}`,
          },
          {
            name: 'username',
            label: 'Username del webhook',
            type: 'text',
            placeholder: 'ZAPHUB Bot',
          },
        ],
      },
      {
        id: 'int_google_sheets',
        name: 'Google Sheets',
        description: 'Lee y escribe datos en hojas de cálculo de Google Sheets',
        category: 'integrations',
        icon: Table,
        color: 'bg-cyan-500',
        outputs: ['rows', 'values', 'updatedRange', 'success'],
        configFields: [
          {
            name: 'serviceAccountKey',
            label: 'Service Account JSON Key',
            type: 'password',
            required: true,
            placeholder: '{"type": "service_account", "project_id": "...", ...}',
            description: 'JSON de la cuenta de servicio de Google Cloud',
          },
          {
            name: 'spreadsheetId',
            label: 'ID de la hoja de cálculo',
            type: 'text',
            required: true,
            placeholder: '1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms',
            description: 'Está en la URL: docs.google.com/spreadsheets/d/[ID]/edit',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'read', label: 'Leer filas' },
              { value: 'append', label: 'Agregar filas nuevas' },
              { value: 'update', label: 'Actualizar filas' },
              { value: 'clear', label: 'Limpiar rango' },
              { value: 'lookup', label: 'Buscar fila por valor' },
            ],
          },
          {
            name: 'sheetName',
            label: 'Nombre de la hoja',
            type: 'text',
            defaultValue: 'Sheet1',
            placeholder: 'Ventas 2026',
          },
          {
            name: 'range',
            label: 'Rango',
            type: 'text',
            placeholder: 'A1:Z1000 o A:Z (toda la hoja)',
          },
          {
            name: 'values',
            label: 'Valores a escribir (JSON)',
            type: 'json',
            placeholder: '[["{{data.name}}", "{{data.email}}", "{{data.amount}}"]]',
            description: 'Array de filas. Cada fila es un array de celdas.',
          },
          {
            name: 'lookupColumn',
            label: 'Columna de búsqueda',
            type: 'text',
            placeholder: 'A o email',
          },
          {
            name: 'lookupValue',
            label: 'Valor a buscar',
            type: 'text',
            placeholder: '{{data.email}}',
          },
        ],
      },
      {
        id: 'int_shopify',
        name: 'Shopify',
        description: 'Gestiona órdenes, productos, clientes e inventario en Shopify',
        category: 'integrations',
        icon: ShoppingCart,
        color: 'bg-cyan-500',
        outputs: ['data', 'id', 'success'],
        configFields: [
          {
            name: 'shopDomain',
            label: 'Dominio de la tienda',
            type: 'text',
            required: true,
            placeholder: 'mitienda.myshopify.com',
          },
          {
            name: 'accessToken',
            label: 'Access Token',
            type: 'password',
            required: true,
            placeholder: 'shpat_...',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'get_orders', label: 'Obtener órdenes' },
              { value: 'get_order', label: 'Obtener orden por ID' },
              { value: 'update_order', label: 'Actualizar orden' },
              { value: 'fulfill_order', label: 'Marcar como enviada' },
              { value: 'get_products', label: 'Obtener productos' },
              { value: 'create_product', label: 'Crear producto' },
              { value: 'update_product', label: 'Actualizar producto' },
              { value: 'update_inventory', label: 'Actualizar inventario' },
              { value: 'get_customers', label: 'Obtener clientes' },
              { value: 'create_discount', label: 'Crear descuento' },
            ],
          },
          {
            name: 'orderId',
            label: 'ID de orden',
            type: 'text',
            placeholder: '{{data.orderId}}',
          },
          {
            name: 'filters',
            label: 'Filtros (JSON)',
            type: 'json',
            placeholder: '{"status": "pending", "financial_status": "paid", "limit": 50}',
          },
          {
            name: 'data',
            label: 'Datos a enviar (JSON)',
            type: 'json',
            placeholder: '{"note": "Procesado por ZAPHUB", "tags": "automatizado"}',
          },
        ],
      },
      {
        id: 'int_airtable',
        name: 'Airtable',
        description: 'Lee y escribe registros en bases de datos Airtable',
        category: 'integrations',
        icon: Database,
        color: 'bg-cyan-500',
        outputs: ['records', 'id', 'success'],
        configFields: [
          {
            name: 'apiKey',
            label: 'API Key (Personal Access Token)',
            type: 'password',
            required: true,
            placeholder: 'patXXXXXX...',
          },
          {
            name: 'baseId',
            label: 'Base ID',
            type: 'text',
            required: true,
            placeholder: 'appXXXXXX',
          },
          {
            name: 'tableId',
            label: 'Tabla',
            type: 'text',
            required: true,
            placeholder: 'tblXXXXXX o nombre de tabla',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'list', label: 'Listar registros' },
              { value: 'get', label: 'Obtener registro por ID' },
              { value: 'create', label: 'Crear registro' },
              { value: 'update', label: 'Actualizar registro' },
              { value: 'delete', label: 'Eliminar registro' },
              { value: 'search', label: 'Buscar con fórmula' },
            ],
          },
          {
            name: 'fields',
            label: 'Campos (JSON)',
            type: 'json',
            placeholder: '{"Name": "{{data.name}}", "Status": "Active", "Amount": {{data.amount}}}',
          },
          {
            name: 'filterFormula',
            label: 'Fórmula de filtro',
            type: 'text',
            placeholder: "{Status} = 'Pending'",
            description: 'Sintaxis de fórmula Airtable',
          },
          {
            name: 'recordId',
            label: 'Record ID',
            type: 'text',
            placeholder: 'recXXXXXX o {{data.airtableId}}',
          },
        ],
      },
      {
        id: 'int_telegram',
        name: 'Telegram Bot',
        description: 'Envía mensajes, fotos y documentos vía Telegram Bot API',
        category: 'integrations',
        icon: MessageCircle,
        color: 'bg-cyan-500',
        outputs: ['messageId', 'success'],
        configFields: [
          {
            name: 'botToken',
            label: 'Bot Token',
            type: 'password',
            required: true,
            placeholder: '123456789:AABBCCDDEEFF...',
            description: 'Obtenido desde @BotFather en Telegram',
          },
          {
            name: 'chatId',
            label: 'Chat ID',
            type: 'text',
            required: true,
            placeholder: '-1001234567890 o {{data.telegramChatId}}',
            description: 'ID del chat, grupo o canal',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            defaultValue: 'send_message',
            options: [
              { value: 'send_message', label: 'Enviar mensaje de texto' },
              { value: 'send_photo', label: 'Enviar imagen' },
              { value: 'send_document', label: 'Enviar documento' },
              { value: 'send_invoice', label: 'Enviar factura (Telegram Pay)' },
            ],
          },
          {
            name: 'text',
            label: 'Mensaje',
            type: 'textarea',
            required: true,
            placeholder: '🔔 *Alerta*: {{data.message}}\n📊 Total: €{{data.amount}}',
          },
          {
            name: 'parseMode',
            label: 'Formato del texto',
            type: 'select',
            defaultValue: 'Markdown',
            options: [
              { value: 'Markdown', label: 'Markdown (*negrita*, _cursiva_)' },
              { value: 'HTML', label: 'HTML (<b>negrita</b>)' },
              { value: 'none', label: 'Sin formato' },
            ],
          },
          {
            name: 'inlineKeyboard',
            label: 'Botones inline (JSON)',
            type: 'json',
            placeholder: '[[{"text": "✅ Aprobar", "callback_data": "approve_{{data.id}}"}, {"text": "❌ Rechazar", "callback_data": "reject_{{data.id}}"}]]',
          },
        ],
      },
      {
        id: 'int_stripe',
        name: 'Stripe',
        description: 'Gestiona pagos, clientes, suscripciones y facturas en Stripe',
        category: 'integrations',
        icon: CreditCard,
        color: 'bg-cyan-500',
        outputs: ['data', 'id', 'success'],
        configFields: [
          {
            name: 'secretKey',
            label: 'Secret Key (sk_live_ o sk_test_)',
            type: 'password',
            required: true,
            placeholder: 'sk_live_...',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'create_customer', label: 'Crear cliente' },
              { value: 'create_payment_intent', label: 'Crear PaymentIntent' },
              { value: 'create_invoice', label: 'Crear y enviar factura' },
              { value: 'create_subscription', label: 'Crear suscripción' },
              { value: 'cancel_subscription', label: 'Cancelar suscripción' },
              { value: 'get_payment', label: 'Obtener pago' },
              { value: 'refund', label: 'Reembolsar pago' },
              { value: 'list_invoices', label: 'Listar facturas' },
            ],
          },
          {
            name: 'customerId',
            label: 'Customer ID',
            type: 'text',
            placeholder: 'cus_... o {{data.stripeCustomerId}}',
          },
          {
            name: 'amount',
            label: 'Cantidad (en céntimos)',
            type: 'number',
            placeholder: '4900 (= €49.00)',
            description: 'Stripe trabaja en unidades mínimas. €49 = 4900',
          },
          {
            name: 'currency',
            label: 'Moneda',
            type: 'select',
            defaultValue: 'eur',
            options: [
              { value: 'eur', label: 'EUR (€)' },
              { value: 'usd', label: 'USD ($)' },
              { value: 'gbp', label: 'GBP (£)' },
              { value: 'mxn', label: 'MXN' },
            ],
          },
          {
            name: 'metadata',
            label: 'Metadata (JSON)',
            type: 'json',
            placeholder: '{"orderId": "{{data.orderId}}", "source": "zaphub"}',
          },
        ],
      },
    ],
  },

  // ─── AI ──────────────────────────────────────────────────────────────────────
  {
    id: 'ai',
    name: 'Inteligencia Artificial',
    icon: Bot,
    color: 'text-pink-500',
    nodes: [
      {
        id: 'ai_claude',
        name: 'Claude AI (Anthropic)',
        description: 'Genera texto, analiza documentos, clasifica datos y toma decisiones con Claude',
        category: 'ai',
        icon: Bot,
        color: 'bg-pink-500',
        outputs: ['text', 'inputTokens', 'outputTokens'],
        docs: 'Claude es ideal para análisis de documentos, clasificación, extracción de datos estructurados y razonamiento complejo.',
        configFields: [
          {
            name: 'model',
            label: 'Modelo',
            type: 'select',
            required: true,
            defaultValue: 'claude-sonnet-4-6',
            options: [
              { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 — Más potente, análisis complejo' },
              { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 — Balanceado (recomendado)' },
              { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — Rápido y económico' },
            ],
          },
          {
            name: 'systemPrompt',
            label: 'System Prompt (instrucciones del rol)',
            type: 'textarea',
            placeholder: 'Eres un asistente experto en análisis financiero. Responde siempre en JSON estructurado. Sé preciso y conciso.',
            description: 'Define el comportamiento y rol de Claude para esta tarea',
          },
          {
            name: 'userMessage',
            label: 'Mensaje / Prompt',
            type: 'textarea',
            required: true,
            placeholder: 'Analiza este email y extrae: remitente, urgencia (1-10), acción requerida y departamento:\n\n{{data.emailBody}}',
            description: 'Usa {{data.campo}} para insertar datos del workflow',
          },
          {
            name: 'outputFormat',
            label: 'Formato de salida esperado',
            type: 'select',
            defaultValue: 'text',
            options: [
              { value: 'text', label: 'Texto libre' },
              { value: 'json', label: 'JSON (parsear automáticamente)' },
              { value: 'markdown', label: 'Markdown' },
            ],
          },
          {
            name: 'jsonSchema',
            label: 'Schema JSON esperado (para validar output)',
            type: 'json',
            placeholder: '{"sender": "string", "urgency": "number", "action": "string", "department": "string"}',
            description: 'Si outputFormat=json, valida que la respuesta tenga esta estructura',
          },
          {
            name: 'temperature',
            label: 'Temperatura (creatividad)',
            type: 'number',
            defaultValue: 0.7,
            description: '0 = determinista/preciso, 1 = creativo/variado',
          },
          {
            name: 'maxTokens',
            label: 'Tokens máximos de respuesta',
            type: 'number',
            defaultValue: 1024,
          },
          {
            name: 'conversationHistory',
            label: 'Historial de conversación (JSON)',
            type: 'json',
            placeholder: '{{data.previousMessages}}',
            description: 'Para mantener conversaciones multi-turno',
          },
        ],
      },
      {
        id: 'ai_chatgpt',
        name: 'ChatGPT / GPT-4 (OpenAI)',
        description: 'Genera texto, código, imágenes y análisis con los modelos GPT de OpenAI',
        category: 'ai',
        icon: Sparkles,
        color: 'bg-pink-500',
        outputs: ['text', 'inputTokens', 'outputTokens'],
        configFields: [
          {
            name: 'apiKey',
            label: 'OpenAI API Key',
            type: 'password',
            required: true,
            placeholder: 'sk-proj-...',
          },
          {
            name: 'model',
            label: 'Modelo',
            type: 'select',
            required: true,
            defaultValue: 'gpt-4o',
            options: [
              { value: 'gpt-4o', label: 'GPT-4o — Multimodal (imágenes + texto)' },
              { value: 'gpt-4o-mini', label: 'GPT-4o Mini — Rápido y económico' },
              { value: 'gpt-4-turbo', label: 'GPT-4 Turbo — Contexto largo (128k)' },
              { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo — Muy económico' },
              { value: 'o1-preview', label: 'o1 Preview — Razonamiento avanzado' },
            ],
          },
          {
            name: 'systemPrompt',
            label: 'System Prompt',
            type: 'textarea',
            placeholder: 'Eres un experto en marketing digital. Responde siempre en español.',
          },
          {
            name: 'userMessage',
            label: 'Mensaje / Prompt',
            type: 'textarea',
            required: true,
            placeholder: 'Escribe un email de seguimiento para el cliente {{data.clientName}} sobre su pedido #{{data.orderId}}',
          },
          {
            name: 'temperature',
            label: 'Temperatura',
            type: 'number',
            defaultValue: 0.7,
          },
          {
            name: 'maxTokens',
            label: 'Tokens máximos',
            type: 'number',
            defaultValue: 1000,
          },
          {
            name: 'responseFormat',
            label: 'Formato de respuesta',
            type: 'select',
            defaultValue: 'text',
            options: [
              { value: 'text', label: 'Texto' },
              { value: 'json_object', label: 'JSON (json_object mode)' },
            ],
          },
        ],
      },
      {
        id: 'ai_classify',
        name: 'Clasificador con IA',
        description: 'Clasifica textos, emails o datos en categorías predefinidas usando IA',
        category: 'ai',
        icon: Brain,
        color: 'bg-pink-500',
        outputs: ['category', 'confidence', 'allScores'],
        docs: 'Ideal para: clasificar emails de soporte, detectar spam, categorizar productos, routing automático.',
        configFields: [
          {
            name: 'model',
            label: 'Modelo AI',
            type: 'select',
            defaultValue: 'claude-haiku-4-5-20251001',
            options: [
              { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku (rápido, económico)' },
              { value: 'claude-sonnet-4-6', label: 'Claude Sonnet (preciso)' },
              { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
            ],
          },
          {
            name: 'inputText',
            label: 'Texto a clasificar',
            type: 'textarea',
            required: true,
            placeholder: '{{data.emailSubject}}: {{data.emailBody}}',
          },
          {
            name: 'categories',
            label: 'Categorías posibles',
            type: 'json',
            required: true,
            placeholder: '["facturacion", "soporte_tecnico", "ventas", "rrhh", "otro"]',
            description: 'Lista de categorías en las que se puede clasificar el texto',
          },
          {
            name: 'categoryDescriptions',
            label: 'Descripción de cada categoría (JSON)',
            type: 'json',
            placeholder: '{"facturacion": "Emails sobre facturas, pagos y cobros", "soporte_tecnico": "Problemas técnicos y bugs"}',
            description: 'Ayuda a la IA a clasificar mejor',
          },
          {
            name: 'multiLabel',
            label: 'Múltiples categorías (multi-label)',
            type: 'boolean',
            defaultValue: false,
          },
        ],
      },
      {
        id: 'ai_extract',
        name: 'Extractor de Datos (IA)',
        description: 'Extrae datos estructurados de textos no estructurados: emails, PDFs, páginas web',
        category: 'ai',
        icon: Search,
        color: 'bg-pink-500',
        outputs: ['extracted', 'confidence'],
        docs: 'Perfecto para: leer facturas PDF, extraer datos de emails, scraping inteligente.',
        configFields: [
          {
            name: 'model',
            label: 'Modelo AI',
            type: 'select',
            defaultValue: 'claude-sonnet-4-6',
            options: [
              { value: 'claude-sonnet-4-6', label: 'Claude Sonnet (recomendado)' },
              { value: 'claude-opus-4-6', label: 'Claude Opus (máxima precisión)' },
              { value: 'gpt-4o', label: 'GPT-4o' },
            ],
          },
          {
            name: 'inputText',
            label: 'Texto de entrada',
            type: 'textarea',
            required: true,
            placeholder: '{{data.emailBody}} o {{data.pdfContent}}',
          },
          {
            name: 'schema',
            label: 'Schema de datos a extraer (JSON)',
            type: 'json',
            required: true,
            placeholder: `{
  "invoiceNumber": "número de factura",
  "totalAmount": "importe total en euros",
  "issueDate": "fecha de emisión (YYYY-MM-DD)",
  "vendor": "nombre del proveedor",
  "items": "array de líneas de producto"
}`,
            description: 'Define qué campos extraer y su descripción',
          },
          {
            name: 'language',
            label: 'Idioma del texto',
            type: 'select',
            defaultValue: 'auto',
            options: [
              { value: 'auto', label: 'Detectar automáticamente' },
              { value: 'es', label: 'Español' },
              { value: 'en', label: 'Inglés' },
            ],
          },
        ],
      },
      {
        id: 'ai_image_gen',
        name: 'Generar Imagen (DALL-E)',
        description: 'Genera imágenes con DALL-E 3 de OpenAI a partir de texto',
        category: 'ai',
        icon: ImageIcon,
        color: 'bg-pink-500',
        outputs: ['imageUrl', 'base64', 'revisedPrompt'],
        configFields: [
          {
            name: 'apiKey',
            label: 'OpenAI API Key',
            type: 'password',
            required: true,
            placeholder: 'sk-proj-...',
          },
          {
            name: 'model',
            label: 'Modelo',
            type: 'select',
            defaultValue: 'dall-e-3',
            options: [
              { value: 'dall-e-3', label: 'DALL-E 3 (alta calidad)' },
              { value: 'dall-e-2', label: 'DALL-E 2 (económico)' },
            ],
          },
          {
            name: 'prompt',
            label: 'Descripción de la imagen',
            type: 'textarea',
            required: true,
            placeholder: 'Un banner profesional para {{data.productName}}, estilo minimalista, fondo blanco, alta calidad',
          },
          {
            name: 'size',
            label: 'Tamaño',
            type: 'select',
            defaultValue: '1024x1024',
            options: [
              { value: '1024x1024', label: '1024×1024 (cuadrado)' },
              { value: '1792x1024', label: '1792×1024 (landscape)' },
              { value: '1024x1792', label: '1024×1792 (portrait)' },
            ],
          },
          {
            name: 'quality',
            label: 'Calidad',
            type: 'select',
            defaultValue: 'standard',
            options: [
              { value: 'standard', label: 'Standard' },
              { value: 'hd', label: 'HD (mayor detalle)' },
            ],
          },
          {
            name: 'style',
            label: 'Estilo',
            type: 'select',
            defaultValue: 'vivid',
            options: [
              { value: 'vivid', label: 'Vivid (dramático)' },
              { value: 'natural', label: 'Natural (fotorrealista)' },
            ],
          },
        ],
      },
      {
        id: 'ai_translate',
        name: 'Traducir con IA',
        description: 'Traduce texto con contexto y precisión superior usando Claude o GPT',
        category: 'ai',
        icon: MessageCircle,
        color: 'bg-pink-500',
        outputs: ['translatedText', 'detectedLanguage'],
        configFields: [
          {
            name: 'model',
            label: 'Modelo',
            type: 'select',
            defaultValue: 'claude-haiku-4-5-20251001',
            options: [
              { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku (rápido)' },
              { value: 'claude-sonnet-4-6', label: 'Claude Sonnet (preciso)' },
              { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
            ],
          },
          {
            name: 'text',
            label: 'Texto a traducir',
            type: 'textarea',
            required: true,
            placeholder: '{{data.content}}',
          },
          {
            name: 'from',
            label: 'Idioma origen',
            type: 'select',
            defaultValue: 'auto',
            options: [
              { value: 'auto', label: 'Detectar automáticamente' },
              { value: 'es', label: 'Español' },
              { value: 'en', label: 'Inglés' },
              { value: 'fr', label: 'Francés' },
              { value: 'de', label: 'Alemán' },
              { value: 'it', label: 'Italiano' },
              { value: 'pt', label: 'Portugués' },
              { value: 'zh', label: 'Chino' },
              { value: 'ja', label: 'Japonés' },
              { value: 'ar', label: 'Árabe' },
            ],
          },
          {
            name: 'to',
            label: 'Idioma destino',
            type: 'select',
            required: true,
            defaultValue: 'en',
            options: [
              { value: 'es', label: 'Español' },
              { value: 'en', label: 'Inglés' },
              { value: 'fr', label: 'Francés' },
              { value: 'de', label: 'Alemán' },
              { value: 'it', label: 'Italiano' },
              { value: 'pt', label: 'Portugués' },
              { value: 'zh', label: 'Chino' },
              { value: 'ja', label: 'Japonés' },
              { value: 'ar', label: 'Árabe' },
            ],
          },
          {
            name: 'context',
            label: 'Contexto (para mejor traducción)',
            type: 'text',
            placeholder: 'Email comercial B2B para sector legal',
            description: 'Describe el contexto para que la IA adapte el tono y vocabulario',
          },
        ],
      },
      {
        id: 'ai_speech_to_text',
        name: 'Transcribir Audio (Whisper)',
        description: 'Transcribe audio a texto con Whisper de OpenAI. Soporta 50+ idiomas.',
        category: 'ai',
        icon: FileAudio,
        color: 'bg-pink-500',
        outputs: ['text', 'language', 'duration', 'segments'],
        configFields: [
          {
            name: 'apiKey',
            label: 'OpenAI API Key',
            type: 'password',
            required: true,
            placeholder: 'sk-proj-...',
          },
          {
            name: 'audioUrl',
            label: 'URL del audio',
            type: 'text',
            placeholder: '{{data.audioUrl}} o https://...',
            description: 'URL pública del archivo de audio (MP3, MP4, WAV, M4A)',
          },
          {
            name: 'language',
            label: 'Idioma del audio',
            type: 'select',
            defaultValue: 'auto',
            options: [
              { value: 'auto', label: 'Detectar automáticamente' },
              { value: 'es', label: 'Español' },
              { value: 'en', label: 'Inglés' },
              { value: 'fr', label: 'Francés' },
              { value: 'de', label: 'Alemán' },
            ],
          },
          {
            name: 'responseFormat',
            label: 'Formato de respuesta',
            type: 'select',
            defaultValue: 'text',
            options: [
              { value: 'text', label: 'Texto plano' },
              { value: 'json', label: 'JSON con timestamps' },
              { value: 'srt', label: 'SRT (subtítulos)' },
              { value: 'vtt', label: 'VTT (subtítulos web)' },
            ],
          },
          {
            name: 'prompt',
            label: 'Prompt de contexto',
            type: 'text',
            placeholder: 'Reunión de equipo sobre finanzas Q1 2026',
            description: 'Ayuda a Whisper con palabras técnicas o nombres específicos',
          },
        ],
      },
    ],
  },

  // ─── BLOCKCHAIN / CRYPTO ─────────────────────────────────────────────────────
  {
    id: 'blockchain',
    name: 'Blockchain & Crypto',
    icon: Shield,
    color: 'text-yellow-500',
    nodes: [
      {
        id: 'blockchain_polygon',
        name: 'Polygon / Ethereum',
        description: 'Interactúa con contratos inteligentes, envía transacciones y lee eventos on-chain',
        category: 'blockchain',
        icon: Shield,
        color: 'bg-yellow-500',
        outputs: ['txHash', 'receipt', 'blockNumber', 'gasUsed', 'data'],
        docs: 'Conecta con InfraCoinn y cualquier contrato en Polygon Mainnet o Mumbai Testnet.',
        configFields: [
          {
            name: 'network',
            label: 'Red',
            type: 'select',
            required: true,
            defaultValue: 'polygon',
            options: [
              { value: 'polygon', label: 'Polygon Mainnet' },
              { value: 'polygon_mumbai', label: 'Polygon Mumbai (Testnet)' },
              { value: 'ethereum', label: 'Ethereum Mainnet' },
              { value: 'sepolia', label: 'Ethereum Sepolia (Testnet)' },
              { value: 'bsc', label: 'Binance Smart Chain' },
            ],
          },
          {
            name: 'rpcUrl',
            label: 'RPC URL (opcional, usa default si vacío)',
            type: 'text',
            placeholder: 'https://polygon-rpc.com',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'call_contract', label: 'Llamar función de contrato (lectura)' },
              { value: 'send_transaction', label: 'Enviar transacción (escritura)' },
              { value: 'get_balance', label: 'Consultar balance de wallet' },
              { value: 'get_transaction', label: 'Obtener datos de transacción' },
              { value: 'listen_events', label: 'Escuchar eventos del contrato' },
              { value: 'transfer_token', label: 'Transferir tokens ERC-20' },
              { value: 'mint_nft', label: 'Mintear NFT ERC-721' },
              { value: 'get_token_balance', label: 'Balance de token ERC-20' },
            ],
          },
          {
            name: 'contractAddress',
            label: 'Dirección del contrato',
            type: 'text',
            placeholder: '0x...',
          },
          {
            name: 'contractAbi',
            label: 'ABI del contrato (JSON)',
            type: 'json',
            placeholder: '[{"inputs": [], "name": "totalSupply", "outputs": [{"type": "uint256"}], "type": "function"}]',
          },
          {
            name: 'functionName',
            label: 'Nombre de la función',
            type: 'text',
            placeholder: 'transfer o balanceOf',
          },
          {
            name: 'functionArgs',
            label: 'Argumentos de la función (JSON)',
            type: 'json',
            placeholder: '["0xRecipient...", "1000000000000000000"]',
            description: 'Array de argumentos en orden. 1 MATIC = 10^18 wei.',
          },
          {
            name: 'privateKey',
            label: 'Private Key del wallet (para transacciones)',
            type: 'password',
            placeholder: '0x...',
            description: '⚠️ Usa siempre variables de entorno. Nunca hardcodees claves.',
          },
          {
            name: 'walletAddress',
            label: 'Dirección del wallet',
            type: 'text',
            placeholder: '0x... o {{data.walletAddress}}',
          },
          {
            name: 'gasLimit',
            label: 'Gas Limit',
            type: 'number',
            placeholder: '200000',
            description: 'Límite de gas. Vacío = estimado automáticamente.',
          },
          {
            name: 'waitForConfirmation',
            label: 'Esperar confirmación',
            type: 'boolean',
            defaultValue: true,
            description: 'Esperar a que la transacción sea confirmada en la blockchain',
          },
          {
            name: 'confirmations',
            label: 'Número de confirmaciones',
            type: 'number',
            defaultValue: 1,
          },
        ],
      },
      {
        id: 'blockchain_wallet_verify',
        name: 'Verificar Firma / Wallet',
        description: 'Verifica firmas criptográficas y autenticación de wallets Web3',
        category: 'blockchain',
        icon: Key,
        color: 'bg-yellow-500',
        outputs: ['isValid', 'signerAddress', 'recovered'],
        configFields: [
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'verify_signature', label: 'Verificar firma ECDSA' },
              { value: 'verify_ownership', label: 'Verificar propiedad de NFT' },
              { value: 'sign_message', label: 'Firmar mensaje (con private key)' },
            ],
          },
          {
            name: 'message',
            label: 'Mensaje original',
            type: 'text',
            placeholder: 'Login ZAPHUB: {{data.nonce}}',
          },
          {
            name: 'signature',
            label: 'Firma hex',
            type: 'text',
            placeholder: '0x...',
          },
          {
            name: 'expectedAddress',
            label: 'Dirección esperada del firmante',
            type: 'text',
            placeholder: '0x... o {{data.walletAddress}}',
          },
          {
            name: 'nftContract',
            label: 'Contrato NFT (para verificar propiedad)',
            type: 'text',
            placeholder: '0x...',
          },
          {
            name: 'tokenId',
            label: 'Token ID del NFT',
            type: 'text',
            placeholder: '{{data.tokenId}}',
          },
        ],
      },
      {
        id: 'blockchain_price',
        name: 'Precios Crypto (CoinGecko)',
        description: 'Obtiene precios en tiempo real de cualquier criptomoneda',
        category: 'blockchain',
        icon: BarChart2,
        color: 'bg-yellow-500',
        outputs: ['price', 'change24h', 'marketCap', 'volume', 'allPrices'],
        configFields: [
          {
            name: 'coins',
            label: 'Monedas (IDs de CoinGecko)',
            type: 'text',
            required: true,
            placeholder: 'bitcoin,ethereum,matic-network',
            description: 'IDs según CoinGecko. Ej: bitcoin, ethereum, matic-network',
          },
          {
            name: 'currency',
            label: 'Moneda de referencia',
            type: 'select',
            defaultValue: 'eur',
            options: [
              { value: 'eur', label: 'EUR (€)' },
              { value: 'usd', label: 'USD ($)' },
              { value: 'btc', label: 'BTC' },
            ],
          },
          {
            name: 'include24hChange',
            label: 'Incluir cambio 24h',
            type: 'boolean',
            defaultValue: true,
          },
          {
            name: 'alertThreshold',
            label: 'Alertar si cambia más de X%',
            type: 'number',
            placeholder: '5',
            description: 'Útil para disparar alertas automáticas',
          },
        ],
      },
    ],
  },

  // ─── SOCIAL MEDIA & CONTENT ──────────────────────────────────────────────────
  {
    id: 'social',
    name: 'Redes Sociales',
    icon: Users,
    color: 'text-rose-500',
    nodes: [
      {
        id: 'social_twitter',
        name: 'Twitter / X',
        description: 'Publica tweets, hilos, respuestas y monitorea menciones',
        category: 'social',
        icon: MessageSquare,
        color: 'bg-rose-500',
        outputs: ['tweetId', 'url', 'likes', 'retweets'],
        configFields: [
          {
            name: 'apiKey',
            label: 'API Key',
            type: 'password',
            required: true,
            placeholder: 'Twitter Developer App API Key',
          },
          {
            name: 'apiSecret',
            label: 'API Secret',
            type: 'password',
            required: true,
          },
          {
            name: 'accessToken',
            label: 'Access Token',
            type: 'password',
            required: true,
          },
          {
            name: 'accessTokenSecret',
            label: 'Access Token Secret',
            type: 'password',
            required: true,
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'post_tweet', label: 'Publicar tweet' },
              { value: 'post_thread', label: 'Publicar hilo (thread)' },
              { value: 'reply', label: 'Responder a tweet' },
              { value: 'retweet', label: 'Retweetear' },
              { value: 'like', label: 'Dar like' },
              { value: 'search', label: 'Buscar tweets' },
              { value: 'get_mentions', label: 'Obtener menciones' },
            ],
          },
          {
            name: 'text',
            label: 'Texto del tweet',
            type: 'textarea',
            placeholder: '{{data.postContent}} #automation #zaphub',
            description: 'Máx 280 caracteres. Soporta variables {{}}.',
          },
          {
            name: 'mediaUrls',
            label: 'URLs de imágenes/vídeo',
            type: 'text',
            placeholder: '{{data.imageUrl}}',
            description: 'URL pública de la imagen o vídeo a adjuntar',
          },
          {
            name: 'replyToId',
            label: 'ID del tweet al que responder',
            type: 'text',
            placeholder: '{{data.tweetId}}',
          },
          {
            name: 'searchQuery',
            label: 'Query de búsqueda',
            type: 'text',
            placeholder: '#bitcoin lang:es -is:retweet',
          },
        ],
      },
      {
        id: 'social_instagram',
        name: 'Instagram (Meta Graph API)',
        description: 'Publica posts, stories y reels en Instagram Business',
        category: 'social',
        icon: ImageIcon,
        color: 'bg-rose-500',
        outputs: ['postId', 'permalink', 'success'],
        configFields: [
          {
            name: 'accessToken',
            label: 'Meta Access Token',
            type: 'password',
            required: true,
            placeholder: 'EAA...',
            description: 'Token de cuenta Instagram Business vía Meta Graph API',
          },
          {
            name: 'igUserId',
            label: 'Instagram User ID',
            type: 'text',
            required: true,
            placeholder: '17841400...',
          },
          {
            name: 'operation',
            label: 'Tipo de contenido',
            type: 'select',
            required: true,
            options: [
              { value: 'image_post', label: 'Publicar imagen' },
              { value: 'carousel', label: 'Publicar carrusel (múltiples imágenes)' },
              { value: 'video_post', label: 'Publicar vídeo/Reel' },
              { value: 'story', label: 'Publicar Story' },
            ],
          },
          {
            name: 'imageUrl',
            label: 'URL de la imagen',
            type: 'text',
            required: true,
            placeholder: '{{data.imageUrl}} (URL pública)',
          },
          {
            name: 'caption',
            label: 'Caption / Descripción',
            type: 'textarea',
            placeholder: '{{data.caption}}\n\n#automation #marketing #zaphub',
          },
          {
            name: 'locationId',
            label: 'Ubicación (Location ID)',
            type: 'text',
            placeholder: 'ID de ubicación de Facebook',
          },
        ],
      },
      {
        id: 'social_linkedin',
        name: 'LinkedIn',
        description: 'Publica posts, artículos y gestiona páginas de empresa',
        category: 'social',
        icon: Link2,
        color: 'bg-rose-500',
        outputs: ['postId', 'url', 'success'],
        configFields: [
          {
            name: 'accessToken',
            label: 'LinkedIn Access Token',
            type: 'password',
            required: true,
            placeholder: 'AQV...',
            description: 'OAuth 2.0 token de LinkedIn API',
          },
          {
            name: 'authorType',
            label: 'Publicar como',
            type: 'select',
            required: true,
            options: [
              { value: 'person', label: 'Perfil personal' },
              { value: 'organization', label: 'Página de empresa' },
            ],
          },
          {
            name: 'authorId',
            label: 'ID del autor/empresa',
            type: 'text',
            required: true,
            placeholder: 'urn:li:person:xxx o urn:li:organization:xxx',
          },
          {
            name: 'operation',
            label: 'Tipo de contenido',
            type: 'select',
            required: true,
            options: [
              { value: 'text_post', label: 'Post de texto' },
              { value: 'image_post', label: 'Post con imagen' },
              { value: 'article', label: 'Artículo' },
              { value: 'document_post', label: 'Post con documento PDF' },
            ],
          },
          {
            name: 'text',
            label: 'Texto del post',
            type: 'textarea',
            required: true,
            placeholder: '{{data.content}}\n\n¿Qué os parece? 👇',
          },
          {
            name: 'visibility',
            label: 'Visibilidad',
            type: 'select',
            defaultValue: 'PUBLIC',
            options: [
              { value: 'PUBLIC', label: 'Público' },
              { value: 'CONNECTIONS', label: 'Solo conexiones' },
            ],
          },
          {
            name: 'mediaUrl',
            label: 'URL de imagen/documento',
            type: 'text',
            placeholder: '{{data.imageUrl}}',
          },
        ],
      },
      {
        id: 'social_youtube',
        name: 'YouTube',
        description: 'Sube vídeos, actualiza metadatos y gestiona comentarios',
        category: 'social',
        icon: Video,
        color: 'bg-rose-500',
        outputs: ['videoId', 'url', 'success'],
        configFields: [
          {
            name: 'clientId',
            label: 'Google OAuth Client ID',
            type: 'text',
            required: true,
            placeholder: 'xxxx.apps.googleusercontent.com',
          },
          {
            name: 'refreshToken',
            label: 'Refresh Token (OAuth)',
            type: 'password',
            required: true,
            placeholder: '1//...',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'upload_video', label: 'Subir vídeo' },
              { value: 'update_video', label: 'Actualizar título/descripción' },
              { value: 'add_to_playlist', label: 'Añadir a playlist' },
              { value: 'get_analytics', label: 'Obtener analytics del canal' },
              { value: 'list_comments', label: 'Listar comentarios' },
              { value: 'reply_comment', label: 'Responder comentario' },
            ],
          },
          {
            name: 'videoUrl',
            label: 'URL del vídeo a subir',
            type: 'text',
            placeholder: '{{data.videoUrl}} (URL pública o ruta local)',
          },
          {
            name: 'title',
            label: 'Título del vídeo',
            type: 'text',
            placeholder: '{{data.videoTitle}}',
          },
          {
            name: 'description',
            label: 'Descripción',
            type: 'textarea',
            placeholder: '{{data.videoDescription}}\n\n🔔 Suscríbete para más contenido',
          },
          {
            name: 'tags',
            label: 'Etiquetas (separadas por coma)',
            type: 'text',
            placeholder: 'tecnología,automatización,ai',
          },
          {
            name: 'privacyStatus',
            label: 'Privacidad',
            type: 'select',
            defaultValue: 'public',
            options: [
              { value: 'public', label: 'Público' },
              { value: 'private', label: 'Privado' },
              { value: 'unlisted', label: 'No listado' },
              { value: 'scheduled', label: 'Programado' },
            ],
          },
          {
            name: 'scheduledAt',
            label: 'Fecha de publicación programada',
            type: 'text',
            placeholder: '{{data.publishAt}} (ISO 8601)',
          },
        ],
      },
      {
        id: 'social_tiktok',
        name: 'TikTok',
        description: 'Publica vídeos y obtiene métricas de TikTok',
        category: 'social',
        icon: Video,
        color: 'bg-rose-500',
        outputs: ['videoId', 'shareUrl', 'success'],
        configFields: [
          {
            name: 'accessToken',
            label: 'TikTok Access Token',
            type: 'password',
            required: true,
            placeholder: 'act...',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'upload_video', label: 'Subir vídeo' },
              { value: 'get_user_info', label: 'Info del usuario' },
              { value: 'get_video_list', label: 'Lista de vídeos' },
            ],
          },
          {
            name: 'videoUrl',
            label: 'URL del vídeo',
            type: 'text',
            required: true,
            placeholder: '{{data.videoUrl}}',
          },
          {
            name: 'title',
            label: 'Título/Caption',
            type: 'textarea',
            placeholder: '{{data.caption}} #fyp #viral',
          },
          {
            name: 'privacyLevel',
            label: 'Privacidad',
            type: 'select',
            defaultValue: 'PUBLIC_TO_EVERYONE',
            options: [
              { value: 'PUBLIC_TO_EVERYONE', label: 'Público' },
              { value: 'MUTUAL_FOLLOW_FRIENDS', label: 'Amigos mutuos' },
              { value: 'SELF_ONLY', label: 'Solo yo' },
            ],
          },
        ],
      },
      {
        id: 'social_wordpress',
        name: 'WordPress',
        description: 'Publica posts, páginas y gestiona contenido en WordPress',
        category: 'social',
        icon: FileText,
        color: 'bg-rose-500',
        outputs: ['postId', 'url', 'success'],
        configFields: [
          {
            name: 'siteUrl',
            label: 'URL del sitio WordPress',
            type: 'text',
            required: true,
            placeholder: 'https://misite.com',
          },
          {
            name: 'username',
            label: 'Usuario',
            type: 'text',
            required: true,
          },
          {
            name: 'appPassword',
            label: 'Application Password',
            type: 'password',
            required: true,
            placeholder: 'xxxx xxxx xxxx xxxx',
            description: 'Generar en WordPress → Usuarios → Contraseñas de aplicación',
          },
          {
            name: 'operation',
            label: 'Operación',
            type: 'select',
            required: true,
            options: [
              { value: 'create_post', label: 'Crear post' },
              { value: 'update_post', label: 'Actualizar post' },
              { value: 'create_page', label: 'Crear página' },
              { value: 'get_posts', label: 'Obtener posts' },
              { value: 'upload_media', label: 'Subir imagen/media' },
            ],
          },
          {
            name: 'title',
            label: 'Título',
            type: 'text',
            required: true,
            placeholder: '{{data.title}}',
          },
          {
            name: 'content',
            label: 'Contenido (HTML)',
            type: 'textarea',
            required: true,
            placeholder: '{{data.htmlContent}}',
          },
          {
            name: 'status',
            label: 'Estado de publicación',
            type: 'select',
            defaultValue: 'publish',
            options: [
              { value: 'publish', label: 'Publicar inmediatamente' },
              { value: 'draft', label: 'Guardar como borrador' },
              { value: 'future', label: 'Programar' },
            ],
          },
          {
            name: 'categories',
            label: 'Categorías (IDs separados por coma)',
            type: 'text',
            placeholder: '1,5,12',
          },
          {
            name: 'featuredImageUrl',
            label: 'URL imagen destacada',
            type: 'text',
            placeholder: '{{data.imageUrl}}',
          },
        ],
      },
    ],
  },
];
